import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import { STAFF_FONT, tone } from "./ui";

/** "2026-11-14" → "Sat, Nov 14" (with year when asked). Dates are wall-clock. */
export function formatDay(iso: string, withYear = false): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** "08:00:00" → "8:00 AM". */
export function formatTime(value: string): string {
  const [h, m] = value.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

const boxSx = {
  position: "relative",
  height: 34,
  boxSizing: "border-box",
  width: "100%",
  border: `1px solid ${tone.line}`,
  borderRadius: "6px",
  px: "10px",
  display: "flex",
  alignItems: "center",
  font: "inherit", fontWeight: 500, fontSize: 13.5,
  color: tone.ink,
  bgcolor: tone.surface,
  whiteSpace: "nowrap",
  overflow: "hidden",
  "&:focus-within": { borderColor: tone.brand, boxShadow: `0 0 0 3px ${tone.brandRing}` },
} as const;

const overlaySx = {
  position: "absolute",
  inset: 0,
  opacity: 0,
  width: "100%",
  height: "100%",
  border: 0,
  p: 0,
  m: 0,
  cursor: "pointer",
  font: "inherit",
} as const;

/**
 * A field that reads like the mockups ("Sat, Nov 14", "8:00 AM") while the
 * native date/time input underneath handles picking and keyboard entry.
 */
export function PickerField({
  type,
  value,
  onChange,
  label,
  display,
  id,
  sx,
  disabled,
}: {
  type: "date" | "time";
  value: string;
  onChange: (value: string) => void;
  label: string;
  display: string;
  id?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
}) {
  return (
    <Box sx={[boxSx, disabled ? { bgcolor: tone.page, color: tone.subtle } : {}, ...(Array.isArray(sx) ? sx : [sx])]}>
      <span aria-hidden="true">{display}</span>
      <Box
        aria-label={label}
        component="input"
        disabled={disabled}
        id={id}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) onChange(type === "time" ? `${event.target.value.slice(0, 5)}:00` : event.target.value);
        }}
        onClick={(event: React.MouseEvent<HTMLInputElement>) => {
          try {
            event.currentTarget.showPicker?.();
          } catch {
            /* showPicker is optional */
          }
        }}
        step={type === "time" ? 300 : undefined}
        sx={overlaySx}
        type={type}
        value={type === "time" ? value.slice(0, 5) : value}
      />
    </Box>
  );
}
